import { Alert, Button, Link, Spinner } from '@amzn/awsui-components-react-v3';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { usePapaParse } from 'react-papaparse';
import { useMutation } from '@apollo/client';
import { v4 as uuid } from 'uuid';
import FileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';
import { CREATE_CONTAINERS } from '../../../services/container-management/queries';

const FQ_CONTAINER_NAME = 'Fully Qualified Container Name';

const CreateContainer = ({
  data,
  csvData,
  csvFileName,
  setCsvData,
  setCsvFileName,
  setFailedRequests,
  hasSubmitted,
  setHasSubmitted,
}: {
  data: any;
  csvData: any;
  csvFileName: string;
  setCsvData: (a: any) => void;
  setCsvFileName: (a: string) => void;
  setFailedRequests: (a: any) => void;
  setHasSubmitted: (a: any) => void;
  hasSubmitted: boolean;
}): ReactElement => {
  const { jsonToCSV } = usePapaParse();
  const { t } = useTranslation();

  const [createContainersBulkResponseFlex, setCreateContainersBulkResponseFlex] = useState<any>([]);
  const [createContainersBulkFailureFlex, setCreateContainersBulkFailureFlex] = useState<any>([]);
  const [createContainersBulkResponse, setCreateContainersBulkResponse] = useState<any>([]);
  const [createContainersBulkFailure, setCreateContainersBulkFailure] = useState<any>([]);
  const [visibleSuccess, setVisibleSuccess] = useState(true);
  const [visibleFailure, setVisibleFailure] = useState(true);
  const [createContainerError, setCreateContainerError] = useState<string>('');
  const updateCsv = (containers: any, siteRoom: any) => {
    const now = new Date();
    const newCsvFileName =
      // eslint-disable-next-line prefer-template
      `CreatedBins${now.getFullYear()}-${now.getMonth()}-${now.getDate()}-` +
      `${now.getHours().toString()}-${now.getMinutes().toString()}-${now.getSeconds().toString()}`;

    const rows: any[] = [];

    containers.forEach((container: any) => {
      // Add successful CreateContainer response to CSV data.
      const metadata = [
        container.containerInstance.metadata?.alphabetIdentifier,
        container.containerInstance.metadata?.section,
        container.containerInstance.metadata?.aisle,
        container.containerInstance.metadata?.level,
        container.containerInstance.containerName,
      ]
        .filter(Boolean)
        .join('');

      const fqContainerName = [siteRoom, metadata]
        .filter(Boolean) //  removes all undefined, empty values
        .join('.');

      const newCsvRow = {
        'Container ID': container.containerInstance.containerId,
        [FQ_CONTAINER_NAME]: fqContainerName,
        Active: 'true',
      };
      rows.push(newCsvRow);
    });

    setCsvData(rows);
    setCsvFileName(newCsvFileName);
  };

  const [createBulk, { loading: creatingBulkContainer }] = useMutation(CREATE_CONTAINERS, {
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const success: any[] = [];
      const failure: any[] = [];
      const { siteRoom } = data.binDetails[0];
      result.createContainers.createdContainers.forEach((item: any) => {
        if (item.error || item.containerInstance.containerId === null) {
          failure.push({
            id: uuid(),
            siteRoom,
            section: item.containerInstance.metadata?.section,
            alphabet: item.containerInstance.metadata?.alphabetIdentifier,
            aisle: item.containerInstance.metadata?.aisle,
            level: item.containerInstance.metadata?.level,
            cell: item.containerInstance.containerName,
            errors: {
              siteRoom: '',
              section: '',
              alphabet: '',
              aisle: '',
              level: '',
              cell: item.error,
            },
          });
        } else {
          success.push(item);
        }
      });
      updateCsv(success, siteRoom);
      setCreateContainersBulkResponse(success);
      setCreateContainersBulkFailure(failure);
      setCreateContainerError('');
    },
    onError: (error) => {
      setCreateContainerError(error.message);
    },
  });

  const [createBulkFlex, { loading: creatingContainersFlex }] = useMutation(CREATE_CONTAINERS, {
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const success: any[] = [];
      const failure: any[] = [];
      const { siteRoom } = data.binDetailsFlex[0];
      result.createContainers.createdContainers.forEach((item: any) => {
        if (item.error) {
          failure.push({
            id: uuid(),
            siteRoom,
            cell: item.containerInstance.containerName,
            errors: {
              siteRoom: '',
              cell: item.error,
            },
          });
        } else {
          success.push(item);
        }
      });
      updateCsv(success, siteRoom);
      setCreateContainersBulkResponseFlex(success);
      setCreateContainersBulkFailureFlex(failure);
      setCreateContainerError('');
    },
    onError: (error) => {
      setCreateContainerError(error.message);
    },
  });

  // Calls createContainer API for the list of bins
  const createAlertsFlex = useCallback(async () => {
    if (data.binDetailsFlex) {
      const requests: any[] = [];
      data.binDetailsFlex.forEach((item: any) => {
        requests.push({
          containerDefinitionId: data.formValues.containerDefinitionId,
          size: data.formValues.size,
          locationId: data.formValues.roomLocationId,
          metadataStruct: {
            isCompliant: true,
          },
          metadata: {},
          containerName: item.cell,
        });
      });
      await createBulkFlex({
        variables: {
          containers: requests,
        },
      });
    }
  }, [
    createBulkFlex,
    data.binDetailsFlex,
    data.formValues.containerDefinitionId,
    data.formValues.roomLocationId,
    data.formValues.size,
  ]);

  // Calls createContainer API for the list of bins
  const createAlerts = useCallback(async () => {
    if (data.binDetails) {
      const requests: any[] = [];
      data.binDetails.forEach((item: any) => {
        requests.push({
          containerDefinitionId: data.formValues.containerDefinitionId,
          size: data.formValues.size,
          locationId: data.formValues.roomLocationId,
          metadata: {},
          metadataStruct: {
            section: item.section,
            aisle: item.aisle,
            level: item.level,
            alphabetIdentifier: item.alphabet,
            isCompliant: true,
          },
          containerName: item.cell,
        });
      });
      await createBulk({
        variables: {
          containers: requests,
        },
      });
    }
  }, [
    createBulk,
    data.binDetails,
    data.formValues.containerDefinitionId,
    data.formValues.roomLocationId,
    data.formValues.size,
  ]);

  useEffect(() => {
    if (hasSubmitted) {
      setVisibleSuccess(true);
      setVisibleFailure(true);
      if (data.binDetails.length > 0) {
        createAlerts();
      }
      if (data.binDetailsFlex.length > 0) {
        createAlertsFlex();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubmitted]);

  const downloadCsv = () => {
    const blob = new Blob([`${jsonToCSV(csvData)}`], { type: 'text/csv;charset=utf-8;' });
    FileSaver.saveAs(blob, `${csvFileName}.csv`);
  };

  // Creates success Alerts
  const renderSuccessAlerts = (values: any[]) => {
    if (values.length > 0) {
      return (
        <Alert
          onDismiss={() => setVisibleSuccess(false)}
          visible={visibleSuccess}
          dismissAriaLabel="Close alert"
          action={
            values.length === 1 && (
              <Link
                external
                externalIconAriaLabel="Opens in a new tab"
                href={`/assets-inventory/container/${values[0].containerInstance.containerId}`}
              >
                {t('View it here')}
              </Link>
            )
          }
          dismissible
          type="success"
        >
          {`${values.length} containers have been successfully created. `}
          <Button className="link-button" variant="link" onClick={downloadCsv}>
            {t('Download Created Bins')}
          </Button>
        </Alert>
      );
    }
    return <div />;
  };
  const setFailure = () => {
    setVisibleFailure(false);
    setVisibleSuccess(false);
    setHasSubmitted(false);
    setFailedRequests({
      formValues: data.formValues,
      binDetails: createContainersBulkFailure,
      binDetailsFlex: createContainersBulkFailureFlex,
      flex: data.flex,
      virtual: data.virtual,
    });
  };

  useEffect(() => {
    if (createContainersBulkFailure.length === 1 || createContainersBulkFailureFlex.length === 1) {
      setVisibleSuccess(true);
      setVisibleFailure(true);
      setHasSubmitted(false);
      setFailedRequests({
        formValues: data.formValues,
        binDetails: createContainersBulkFailure,
        binDetailsFlex: createContainersBulkFailureFlex,
        flex: data.flex,
        virtual: data.virtual,
      });
    }
  }, [
    createContainersBulkFailure,
    createContainersBulkFailureFlex,
    data.flex,
    data.formValues,
    data.virtual,
    setFailedRequests,
    setHasSubmitted,
  ]);

  // Creates failure Alerts
  const renderFailureAlerts = (values: any[]) => {
    let errorMessage = '';
    let linkText = '';
    if (values.length > 0) {
      if (values.length === 1) {
        errorMessage = values[0].errors.cell;
      } else {
        errorMessage = `${values.length} containers failed to create.`;
        linkText = ' Fix them here';
      }
      return (
        <Alert
          onDismiss={() => setVisibleFailure(false)}
          visible={visibleFailure}
          dismissAriaLabel="Close alert"
          dismissible
          type="error"
        >
          {errorMessage}
          <Button className="setFailureButton" variant="link" onClick={setFailure}>
            {linkText}
          </Button>
        </Alert>
      );
    }
    return <div />;
  };

  if (creatingContainersFlex || creatingBulkContainer) {
    return <Spinner size="big" />;
  }

  return (
    <div>
      {createContainerError && <Alert type="error">{createContainerError}</Alert>}

      {renderSuccessAlerts(createContainersBulkResponse)}
      {renderSuccessAlerts(createContainersBulkResponseFlex)}
      {renderFailureAlerts(createContainersBulkFailure)}
      {renderFailureAlerts(createContainersBulkFailureFlex)}
    </div>
  );
};

export default CreateContainer;
